if (!customElements.get('product-sticky-atc-cottolio')) {
  customElements.define(
    'product-sticky-atc-cottolio',
    class ProductStickyAtcCottolio extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;
        this.soldOutLabel = this.dataset.soldOutLabel || 'Sold Out';

        this.buyAnchor = document.querySelector(
          `#MainProduct-${this.sectionId} .product-main-cottolio__buy`
        );
        this.mainForm = document.getElementById(this.productFormId);
        this.mainSubmit =
          this.mainForm?.querySelector('[name="add"]') ||
          this.mainForm?.querySelector('[type="submit"]');
        this.mainPriceEl = document.getElementById(`price-${this.sectionId}`);
        this.stickySubmit = this.querySelector('[data-sticky-atc-submit]');
        this.stickyLabel = this.querySelector('[data-sticky-atc-label]');
        this.stickyPriceEl = this.querySelector('[data-sticky-atc-price]');
        this.spinner = this.stickySubmit?.querySelector('.loading-overlay__spinner');
        this.optionsRoot = this.querySelector('[data-sticky-atc-options]');
        this.variantData = this.getVariantData();
        this.selectedOptions = this.getSelectedOptionsFromDom();

        if (!this.buyAnchor || !this.mainForm || !this.mainSubmit || !this.stickySubmit) {
          this.remove();
          return;
        }

        if (this.optionsRoot) {
          this.classList.add('product-sticky-atc-cottolio--has-options');
        }

        this.onIntersect = this.onIntersect.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onStickySubmit = this.onStickySubmit.bind(this);
        this.onOptionClick = this.onOptionClick.bind(this);
        this.syncPrice = this.syncPrice.bind(this);
        this.syncSubmitState = this.syncSubmitState.bind(this);

        this.intersectionObserver = new IntersectionObserver(this.onIntersect, {
          threshold: 0,
          rootMargin: '0px 0px -8px 0px',
        });
        this.intersectionObserver.observe(this.buyAnchor);

        if (this.mainPriceEl) {
          this.priceObserver = new MutationObserver(this.syncPrice);
          this.priceObserver.observe(this.mainPriceEl, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['class'],
          });
        }

        this.submitObserver = new MutationObserver(this.syncSubmitState);
        this.submitObserver.observe(this.mainSubmit, {
          attributes: true,
          attributeFilter: ['class', 'aria-disabled', 'disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.stickySubmit.addEventListener('click', this.onStickySubmit);
        document.addEventListener('variant:change', this.onVariantChange);
        this.optionsRoot?.addEventListener('click', this.onOptionClick);

        this.syncPrice();
        this.syncSubmitState();
        this.syncOptionAvailability();
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.submitObserver?.disconnect();
        this.stickySubmit?.removeEventListener('click', this.onStickySubmit);
        document.removeEventListener('variant:change', this.onVariantChange);
        this.optionsRoot?.removeEventListener('click', this.onOptionClick);
        this.setVisible(false);
      }

      getVariantData() {
        const script = this.querySelector('[data-sticky-variants]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return [];
        }
      }

      getMainPicker() {
        return (
          document.getElementById(`variant-radios-${this.sectionId}`) ||
          document.getElementById(`variant-selects-${this.sectionId}`)
        );
      }

      getSelectedOptionsFromDom() {
        const options = [];
        this.querySelectorAll('[data-sticky-option]').forEach((group) => {
          const selected = group.querySelector('[data-sticky-option-value].is-selected');
          options.push(selected?.dataset.value || '');
        });
        return options;
      }

      onIntersect([entry]) {
        this.setVisible(!entry.isIntersecting);
      }

      setVisible(isVisible) {
        this.classList.toggle('is-visible', isVisible);
        this.toggleAttribute('hidden', !isVisible);
        this.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        document.body.classList.toggle('has-sticky-atc-cottolio', isVisible);
      }

      onStickySubmit(event) {
        event.preventDefault();

        if (
          this.mainSubmit.disabled ||
          this.mainSubmit.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.mainSubmit.click();
      }

      onOptionClick(event) {
        const button = event.target.closest('[data-sticky-option-value]');
        if (!button || !this.contains(button)) return;
        if (button.classList.contains('is-disabled') || button.getAttribute('aria-disabled') === 'true') {
          return;
        }

        const mainPicker = this.getMainPicker();
        if (!mainPicker) return;

        const position = Number(button.dataset.optionPosition);
        const value = button.dataset.value;
        if (!position || value == null) return;

        this.selectedOptions[position - 1] = value;
        this.updateStickySelectedUi();
        this.driveMainPicker(mainPicker, position, value);
        // Invalid combos do not emit variant:change; still refresh local UI state.
        this.syncOptionAvailability();
        this.syncSubmitState();
        this.syncPrice();
      }

      driveMainPicker(mainPicker, position, value) {
        const fieldsets = mainPicker.querySelectorAll('fieldset');
        const fieldset = fieldsets[position - 1];
        if (!fieldset) return;

        const radio = [...fieldset.querySelectorAll('input[type="radio"]')].find(
          (input) => input.value === value
        );
        if (radio) {
          if (radio.checked) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const select = fieldset.querySelector('select');
        if (select && select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onVariantChange(event) {
        if (String(event.detail?.sectionId) !== String(this.sectionId)) return;

        const variant = event.detail?.variant;
        if (!variant) return;

        this.selectedOptions = [
          variant.option1,
          variant.option2,
          variant.option3,
        ].filter((value) => value != null && value !== '');

        this.updateStickySelectedUi();
        this.syncOptionAvailability(variant);
        this.syncPrice();
        this.syncSubmitState();
      }

      updateStickySelectedUi() {
        this.querySelectorAll('[data-sticky-option]').forEach((group, index) => {
          const selectedValue = this.selectedOptions[index];
          group.querySelectorAll('[data-sticky-option-value]').forEach((button) => {
            const isSelected = button.dataset.value === selectedValue;
            button.classList.toggle('is-selected', isSelected);
            button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          });
        });
      }

      syncOptionAvailability(currentVariant) {
        if (!this.variantData.length) return;

        const selected = [...this.selectedOptions];
        if (currentVariant) {
          selected[0] = currentVariant.option1;
          if (currentVariant.option2 != null) selected[1] = currentVariant.option2;
          if (currentVariant.option3 != null) selected[2] = currentVariant.option3;
        }

        this.querySelectorAll('[data-sticky-option]').forEach((group, optionIndex) => {
          group.querySelectorAll('[data-sticky-option-value]').forEach((button) => {
            const value = button.dataset.value;
            const available = this.variantData.some((variant) => {
              if (!variant.available) return false;
              if (optionIndex === 0) return variant.option1 === value;
              if (variant.option1 !== selected[0]) return false;
              if (optionIndex === 1) return variant.option2 === value;
              if (variant.option2 !== selected[1]) return false;
              return variant.option3 === value;
            });

            button.classList.toggle('is-disabled', !available);
            button.toggleAttribute('aria-disabled', !available);
          });
        });
      }

      syncPrice() {
        if (!this.mainPriceEl || !this.stickyPriceEl) return;

        const isUnavailable = this.mainPriceEl.classList.contains('visibility-hidden');
        this.stickyPriceEl.classList.toggle('visibility-hidden', isUnavailable);

        const price = this.mainPriceEl.querySelector('.price');
        if (!price) return;

        this.stickyPriceEl.innerHTML = price.outerHTML;
      }

      syncSubmitState() {
        if (!this.mainSubmit || !this.stickySubmit) return;

        const isDisabled =
          this.mainSubmit.disabled ||
          this.mainSubmit.getAttribute('aria-disabled') === 'true';
        const isLoading = this.mainSubmit.classList.contains('loading');
        const mainLabel = this.mainSubmit.querySelector('span');

        this.stickySubmit.disabled = isDisabled;
        this.stickySubmit.toggleAttribute('aria-disabled', isDisabled);
        this.stickySubmit.classList.toggle('loading', isLoading);
        this.spinner?.classList.toggle('hidden', !isLoading);

        if (this.stickyLabel && mainLabel) {
          this.stickyLabel.textContent = mainLabel.textContent.trim();
        } else if (this.stickyLabel && isDisabled) {
          this.stickyLabel.textContent = this.soldOutLabel;
        }
      }
    }
  );
}
